import { useAuthStore } from '../stores/auth';
import type { StreamMessage } from '../types';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private projectId: string;
  private messageHandlers: Set<(message: StreamMessage) => void> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private shouldReconnect = true;
  private lastSyncTimestamp: number | undefined = undefined;
  private onReconnectCallback: (() => Promise<void>) | null = null;
  private onDisconnectedCallback: (() => void) | null = null;
  private isSyncing = false;
  private parseErrorCount = 0;
  private readonly maxParseErrors = 5;
  private messageHandlerErrorCount = 0;
  private readonly maxMessageHandlerErrors = 10;
  private connectResolve: (() => void) | null = null;
  private connectReject: ((error: any) => void) | null = null;

  constructor(projectId: string) {
    this.projectId = projectId;
  }

  /**
   * Connect to the WebSocket endpoint
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/api/projects/${this.projectId}/ws`;

      console.log('Connecting to WebSocket:', wsUrl);

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = async () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;

          // Send JWT authentication first if available
          const authStore = useAuthStore();
          const token = await authStore.getIdToken();

          if (token) {
            console.log('Sending authentication token');
            const impersonateUid = sessionStorage.getItem('impersonate');
            const authMessage: any = { type: 'auth', token };
            if (impersonateUid) {
              authMessage.impersonate = impersonateUid;
            }
            this.send(authMessage);
            // Wait for auth_success before proceeding to sync
            // Auth response will trigger sync in onmessage handler
          } else {
            // No authentication - proceed with sync directly for backward compatibility
            console.log('No auth token available, proceeding with sync');
            await this.performSync();
          }
        };

        this.ws.onmessage = event => {
          try {
            const message = JSON.parse(event.data) as StreamMessage;

            // Handle authentication responses
            if (message.type === 'auth_success') {
              console.log('WebSocket authentication successful');
              this.performSync().catch(error => {
                console.error('Sync failed after authentication:', error);
                if (this.connectReject) {
                  this.connectReject(error);
                  this.connectReject = null;
                  this.connectResolve = null;
                }
              });
              return;
            }

            if (message.type === 'auth_error') {
              console.error('WebSocket authentication failed:', message);
              if (this.connectReject) {
                this.connectReject(new Error('WebSocket authentication failed'));
                this.connectReject = null;
                this.connectResolve = null;
              }
              return;
            }

            // Handle normal messages
            this.notifyHandlers(message);
            this.parseErrorCount = 0; // Reset on success
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error, 'Raw data:', event.data);
            this.parseErrorCount++;

            if (this.parseErrorCount >= this.maxParseErrors) {
              console.error('Too many parse errors, disconnecting');
              this.disconnect(true);

              if (this.onDisconnectedCallback) {
                this.onDisconnectedCallback();
              }
            }
          }
        };

        this.ws.onerror = error => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = event => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.ws = null;

          // Attempt to reconnect if not a normal closure and reconnection is allowed
          const shouldAttemptReconnect =
            event.code !== 1000 && this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts;

          if (shouldAttemptReconnect) {
            this.reconnectAttempts++;
            const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
            console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

            setTimeout(() => {
              if (this.shouldReconnect) {
                this.connect().catch(console.error);
              }
            }, delay);
          } else if (event.code !== 1000 && this.reconnectAttempts >= this.maxReconnectAttempts) {
            // Max reconnection attempts reached
            console.error('WebSocket reconnection failed after max attempts');

            if (this.onDisconnectedCallback) {
              this.onDisconnectedCallback();
            }
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket
   * @param permanent - If true, prevents any reconnection attempts
   */
  disconnect(permanent: boolean = false) {
    if (permanent) {
      this.shouldReconnect = false;
    }
    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }
  }

  /**
   * Add a message handler
   */
  onMessage(handler: (message: StreamMessage) => void) {
    this.messageHandlers.add(handler);

    // Return a cleanup function
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Set callback to execute on reconnection (e.g., to refresh sync timestamp)
   */
  onReconnect(callback: () => Promise<void>) {
    this.onReconnectCallback = callback;
  }

  /**
   * Set callback to execute when max reconnection attempts reached
   */
  onDisconnected(callback: () => void) {
    this.onDisconnectedCallback = callback;
  }

  /**
   * Send a message to the server
   * Returns true if sent successfully, false if WebSocket not connected
   */
  send(message: any): boolean {
    // Store sync timestamp for reconnection
    if (message.type === 'sync' && typeof message.timestamp === 'number') {
      this.lastSyncTimestamp = message.timestamp;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('Cannot send message: WebSocket is not connected', message);
      return false;
    }
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Notify all handlers of a message
   */
  private notifyHandlers(message: StreamMessage) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
        // Reset error count on success
        this.messageHandlerErrorCount = 0;
      } catch (error) {
        console.error('Message handler error:', error, 'Message:', message);
        this.messageHandlerErrorCount++;

        // If we've hit too many consecutive errors, disconnect
        if (this.messageHandlerErrorCount >= this.maxMessageHandlerErrors) {
          console.error('Too many message handler errors, disconnecting WebSocket');
          this.disconnect(true);

          // Notify application of fatal error
          if (this.onDisconnectedCallback) {
            this.onDisconnectedCallback();
          }
        }
      }
    });
  }

  /**
   * Perform sync operation (send sync message or reconnect callback)
   */
  private async performSync(): Promise<void> {
    // Prevent duplicate sync attempts
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping');
      if (this.connectResolve) {
        this.connectResolve();
        this.connectResolve = null;
        this.connectReject = null;
      }
      return;
    }

    try {
      this.isSyncing = true;

      // Handle reconnection sync if we have a previous timestamp
      if (this.lastSyncTimestamp !== undefined) {
        if (this.onReconnectCallback) {
          console.log('Executing reconnect callback to refresh sync timestamp');
          await this.onReconnectCallback();
        } else {
          // Fallback: resend sync message with stored timestamp
          console.log('Resending sync message after reconnect with timestamp:', this.lastSyncTimestamp);
          this.send({ type: 'sync', timestamp: this.lastSyncTimestamp });
        }
      }

      if (this.connectResolve) {
        this.connectResolve();
        this.connectResolve = null;
        this.connectReject = null;
      }
    } catch (error) {
      console.error('WebSocket sync failed:', error);

      // Close the WebSocket since sync failed
      if (this.ws) {
        this.ws.close();
      }

      if (this.connectReject) {
        this.connectReject(error);
        this.connectReject = null;
        this.connectResolve = null;
      }

      throw error;
    } finally {
      this.isSyncing = false;
    }
  }
}
